import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import { UnsplashImage } from "../api/unsplash";
import * as t from "io-ts";
import { isUnsplashImage, isPexelsImage, isPixabayImage } from "./dimensions";
import { MediaInputValue, Image } from "@/types/media";

export const MediaUploadResponseV = t.type({
  attachment_id: t.number,
  url: t.string,
  alt: t.string,
  caption: t.string
});

export type MediaUploadResponse = t.TypeOf<typeof MediaUploadResponseV>;

export const UsedStockPhotosResponseV = t.type({
  used_stock_photo_ids: t.array(t.string)
});

export type UsedStockPhotosResponse = t.TypeOf<typeof UsedStockPhotosResponseV>;

// TODO - this function needs to return a proper value, not just want tha tmatches the type definition with the sizes hard-coded
export function getMediaInputValueFromMediaUploadResponse(
  response: MediaUploadResponse
): MediaInputValue {
  const image: Image = {
    id: response.attachment_id,
    filename: `downloaded-image-${response.attachment_id}.jpg`,
    url: response.url,
    alt: response.alt,
    caption: response.caption,
    type: "image",
    mime: "image/jpeg",
    sizes: {
      full: {
        width: 1200,
        height: 800,
        orientation: "landscape",
        url: response.url
      }
    },
    attributes: {}
  };
  
  return image;
}

export function generateStockPhotoId(image: UnsplashImage): string {
  if (isUnsplashImage(image)) {
    return `unsplash-${image.id}`;
  } else if (isPexelsImage(image)) {
    return `pexels-${image.id}`;
  } else if (isPixabayImage(image)) {
    return `pixabay-${image.id}`;
  }
  return `unknown-${image.id}`;
}

export function shouldDownloadImageToMediaLibrary(
  image: UnsplashImage
): boolean {
  // unsplash wants to be hotlinked
  return !isUnsplashImage(image);
}

export async function downloadImageToMediaLibrary(
  imageUrl: string,
  image: UnsplashImage,
  abortController: AbortController
): Promise<MediaUploadResponse | null> {
  try {
    const stockPhotoId = generateStockPhotoId(image);
    const filename = generateFilenameFromImage(image);

    // @ts-ignore
    if (typeof ray !== 'undefined') {
      // @ts-ignore
      ray('📥 Downloading image to media library', {
        stockPhotoId,
        filename,
        imageUrl,
        alt_text: image.alt_description || "",
        caption: image.description || ""
      });
    }

    const result = await makeWordPressAjaxRequest({
      action: "breakdance_download_external_image",
      dataToPost: {
        image_url: imageUrl,
        alt_text: image.alt_description || "",
        caption: image.description || "",
        filename: filename,
        stock_photo_id: stockPhotoId
      },
      decoder: MediaUploadResponseV,
      friendlyName: "Download external image to media library",
      abortController: abortController
    });

    if (result._tag === "Right") {
      // @ts-ignore
      if (typeof ray !== 'undefined') {
        // @ts-ignore
        ray('✅ Image download successful', result.right);
      }
      return result.right;
    } else {
      // @ts-ignore
      if (typeof ray !== 'undefined') {
        // @ts-ignore
        ray('❌ Image download failed', result.left);
      }
      console.error("Failed to download image to media library:", result.left);
      return null;
    }
  } catch (error) {
    // @ts-ignore
    if (typeof ray !== 'undefined') {
      // @ts-ignore
      ray('💥 Image download error', error);
    }
    console.error("Error downloading image to media library:", error);
    return null;
  }
}

export async function getUsedStockPhotoIds(
  abortController: AbortController
): Promise<string[]> {
  try {
    const result = await makeWordPressAjaxRequest({
      action: "breakdance_get_used_stock_photos",
      dataToPost: {},
      decoder: UsedStockPhotosResponseV,
      friendlyName: "Get used stock photos",
      abortController: abortController
    });

    if (result._tag === "Right") {
      return result.right.used_stock_photo_ids;
    } else {
      console.error("Failed to get used stock photos:", result.left);
      return [];
    }
  } catch (error) {
    console.error("Error getting used stock photos:", error);
    return [];
  }
}

function generateFilenameFromImage(image: UnsplashImage): string {
  let filename = "stock-image";

  if (image.alt_description) {
    filename = image.alt_description
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
      .substring(0, 100); // Limit to 100 chars to prevent filesystem issues
  } else if (image.description) {
    filename = image.description
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
      .substring(0, 100); // Limit to 100 chars to prevent filesystem issues
  }

  // Add image ID to ensure uniqueness
  filename = `${filename}-${image.id}`;

  const extension = "jpg";
  return `${filename}.${extension}`;
}
